import Image from "next/image";
import Link from "next/link";
import { useTranslation } from "next-i18next";
import { MdDone, MdOutlineKeyboardDoubleArrowDown } from "react-icons/md";
import BackendPrograms from "@/images/backend_programs.svg";
import FrontendPrograms from "@/images/frontend_programs.svg";
import FullStackPrograms from "@/images/fullstack_programs.svg";
import SoftwareEngineeringPrograms from "@/images/software_programs.svg";

const ACCENT = "#FE3D67";

const CARD_BORDER = {
  background: "linear-gradient(185deg, rgba(29, 54, 118, 0.5) -1.41%, #3A9FDA 85.85%)",
  WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
  WebkitMaskComposite: "xor",
  maskComposite: "exclude",
};

const PROGRAM_KEYS = [
  {
    id: "fullstack",
    image: FullStackPrograms,
    title: "home-page.programs-fullstack-title",
    modules: "home-page.programs-fullstack-modules",
    duration: "home-page.programs-fullstack-duration",
  },
  {
    id: "frontend",
    image: FrontendPrograms,
    title: "home-page.programs-frontend-title",
    modules: "home-page.programs-frontend-modules",
    duration: "home-page.programs-frontend-duration",
  },
  {
    id: "backend",
    image: BackendPrograms,
    title: "home-page.programs-backend-title",
    modules: "home-page.programs-backend-modules",
    duration: "home-page.programs-backend-duration",
  },
  {
    id: "software-engineering",
    image: SoftwareEngineeringPrograms,
    title: "home-page.programs-softwareengineering-title",
    modules: "home-page.programs-softwareengineering-modules",
    duration: "home-page.programs-softwareengineering-duration",
  },
];

const FeatureItem = ({ text }) => (
  <div className="flex items-center gap-3">
    <span
      className="min-w-[25px] min-h-[25px] rounded-full flex items-center justify-center"
      style={{ backgroundColor: `${ACCENT}66` }}
    >
      <MdDone className="w-[18px] h-[18px]" style={{ color: ACCENT }} />
    </span>
    <span className="text-[14px] md:text-[16px] font-[400] text-[#E5E5E5]">{text}</span>
  </div>
);

const ProgramCard = ({ title, image, features, learnMoreLabel }) => (
  <article className="relative rounded-[24px] overflow-hidden bg-[linear-gradient(80.42deg,rgba(255,255,255,0.08)_25.25%,rgba(83,84,108,0.1)_98.05%)] backdrop-blur-[16px]">
    <div className="absolute inset-0 rounded-[24px] p-[1.2px] pointer-events-none" style={CARD_BORDER} />

    <div className="flex flex-col lg:flex-row">
      <div className="w-full lg:w-1/2 px-[16px] py-[32px] md:p-8 lg:p-12 relative">
        <div
          className="absolute opacity-60 w-[1.5px] left-[32px] md:left-[52px] lg:left-[68px] top-[69px] md:top-[77px] lg:top-[93px] bottom-[32px] md:bottom-8 lg:bottom-12"
          style={{ background: `linear-gradient(180deg, ${ACCENT} 0%, rgba(255, 255, 255, 0.01) 100%)` }}
        />

        <div className="flex gap-[20px] relative z-10">
          <div
            className="md:min-w-[40px] md:h-[40px] min-w-[32px] h-[32px] rounded-full flex items-center justify-center mt-0 lg:mt-[5px] flex-shrink-0"
            style={{ backgroundColor: ACCENT }}
          >
            <MdOutlineKeyboardDoubleArrowDown className="text-white md:w-[30px] md:h-[30px] w-[24px] h-[24px]" />
          </div>

          <div className="flex flex-col gap-[24px] w-full min-w-0">
            <h3 className="text-[24px] md:text-[32px] lg:text-[40px] font-[500] text-[#E5E5E5] leading-tight">
              {title}
            </h3>

            <div className="space-y-3 md:space-y-[24px]">
              {features.map((feature) => (
                <FeatureItem key={feature} text={feature} />
              ))}
            </div>

            <Link
              href="/programs"
              aria-label={learnMoreLabel}
              className="w-full sm:w-[220px] h-[48px] px-8 text-[14px] md:text-[16px] font-[500] rounded-full transition-colors duration-300 flex items-center justify-center border border-[#FE3D67] bg-[#FE3D67] text-white hover:bg-white hover:text-[#FE3D67]"
            >
              {learnMoreLabel}
            </Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-6 lg:p-8">
        <Image
          src={image}
          alt={title}
          className="w-full max-w-[411px] h-auto object-contain"
          loading="lazy"
        />
      </div>
    </div>
  </article>
);

const Programs = () => {
  const { t } = useTranslation();
  const scheduleFeature = t("home-page.programs-feature-3");
  const learnMoreLabel = t("home-page.programs-learn-more");

  const programs = PROGRAM_KEYS.map(({ id, image, title, modules, duration }) => ({
    id,
    image,
    title: t(title),
    features: [t(modules), t(duration), scheduleFeature],
  }));

  return (
    <section
      id="programs"
      className="pt-[60px] pb-[60px] md:pt-[100px] md:pb-[150px] bg-gradient-to-b from-[#091F59] to-[#1A3884] md:rounded-[64px] rounded-[24px] mb-[100px] md:mb-[200px] mx-[8px]"
    >
      <div className="max-w-[90%] mx-auto">
        <header className="flex flex-col md:flex-row justify-start md:justify-center gap-0 md:gap-4 mb-12">
          <h2 className="text-[32px] md:text-[48px] lg:text-[64px] font-[500] text-white leading-tight">
            {t("home-page.programs-title-1")}
          </h2>
          <h2 className="text-[32px] md:text-[48px] lg:text-[64px] font-[500] text-[#FF4081] leading-tight">
            {t("home-page.programs-title-2")}
          </h2>
        </header>

        <div className="flex flex-col gap-6 lg:gap-8">
          {programs.map((program) => (
            <ProgramCard key={program.id} {...program} learnMoreLabel={learnMoreLabel} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Programs;
